// Non-production environments get their own cookie domain so their cookies
// never reach production (.mento.co). Mirrors the platform frontend's
// ENVIRONMENT_SUBDOMAINS in src/constants/auth.ts.
const ENVIRONMENT_SUBDOMAINS = ['staging', 'dev'];

/**
 * Returns the parent domain that cookies for this hostname should be scoped
 * to, or null for localhost. Every cookie shared across an environment's
 * subdomains uses this, so a non-production cookie never reaches production.
 */
export const environmentCookieDomain = (hostname: string): string | null => {
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') return null;
  const parts = hostname.split('.');
  if (parts.length < 2) return null;
  const isEnvironmentScoped = ENVIRONMENT_SUBDOMAINS.some((label) => parts.includes(label));
  return `.${parts.slice(isEnvironmentScoped ? -3 : -2).join('.')}`;
};

/**
 * Returns cookie options for Stytch SDK initialization.
 * Sets the cookie domain to the environment's parent domain so SDK cookies
 * match the backend's HttpOnly cookie domain (COOKIE_DOMAIN env var). Without
 * this, duplicate cookies on different domains break MFA and sessions.
 *
 * - Production: moments.mento.co / app.mento.co → .mento.co
 * - Staging: moments.staging.mento.co / staging.mento.co → .staging.mento.co
 * - Development: moments.dev.mento.co / dev.mento.co → .dev.mento.co
 *
 * Returns undefined for localhost so laptop development is unaffected.
 */
export const getStytchCookieOptions = (
  hostname: string = window.location.hostname
): { domain: string; path: string; availableToSubdomains: boolean } | undefined => {
  const parentDomain = environmentCookieDomain(hostname);
  if (!parentDomain) return undefined;
  return { availableToSubdomains: true, domain: parentDomain, path: '/' };
};
